import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { isNative, isAndroid } from '../../utils/capacitor';
import { initPushListeners, pushSupported } from '../../utils/push';

// Bridges the web UI to native OS chrome/behaviours (native builds only):
//   • Status bar — matches the app theme (text style + Android background).
//   • Android hardware back button — navigates within the app instead of doing
//     a raw WebView back, with double-press-to-exit on the root screens.
//   • Push notification tap + FCM token rotation — registered here because this
//     component is always mounted.
// Renders nothing except a transient "press back again" toast.

const ROOT_PATHS = ['/', '/dashboard', '/login', '/register'];

const NativeBridge = () => {
  const { darkMode } = useTheme();
  const [toast, setToast] = useState('');
  const lastBackRef = useRef(0);

  // Mark the document as the native app so the global safe-area CSS applies.
  useEffect(() => {
    if (isNative()) document.documentElement.classList.add('is-native');
  }, []);

  // ── Status bar follows the theme ──────────────────────────────────────────
  useEffect(() => {
    if (!isNative()) return;
    let cancelled = false;
    (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        if (cancelled) return;
        // Dark UI → light (white) status-bar text, and vice versa.
        await StatusBar.setStyle({ style: darkMode ? Style.Light : Style.Dark });
        if (isAndroid()) {
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setBackgroundColor({ color: darkMode ? '#1a1438' : '#ffffff' });
        }
      } catch (_) { /* plugin missing on web build — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [darkMode]);

  // ── Push: notification tap → deep link, and FCM token rotation ────────────
  // These listeners used to live only in SettingsPage, so they existed only
  // while that one page was mounted. Two things broke as a result:
  //   • Tapping a bedtime reminder opened the app but never navigated — the
  //     notification's deep-link URL was simply dropped, because nothing was
  //     listening for notificationActionPerformed.
  //   • FCM tokens rotate. tokenReceived was only synced to the backend while
  //     Settings was open, so a user who never returned there kept a stale
  //     token on the server and silently stopped receiving notifications.
  // NativeBridge is mounted for the whole session, which is where both belong.
  // SettingsPage keeps its own foreground-banner listener; a duplicate
  // registration there is harmless (same URL, idempotent token POST).
  //
  // On the web, only in a browser that has allowed notifications: none can
  // arrive anywhere else. Starting it for every visitor initialised Firebase on
  // every page view, which opens its IndexedDB databases — wasted work, and a
  // crawler that wipes storage between pages force-closed them, reaching Sentry
  // as "UnknownError: Connection is closing because of: Force close delete
  // origin". Enabling push in Settings starts Firebase itself.
  useEffect(() => {
    if (!pushSupported()) return undefined;
    if (!isNative() && !(typeof Notification !== 'undefined' && Notification.permission === 'granted')) return undefined;
    return initPushListeners();
  }, []);

  // ── Clear delivered notifications when the app is opened ──────────────────
  // The icon badge and Android's notification dot persist until something
  // clears them, and nothing did: a reminder read days ago still marked the
  // app as having something waiting. Runs on launch and on every resume, since
  // the common case is tapping the notification itself — which foregrounds the
  // app without a fresh launch.
  useEffect(() => {
    if (!isNative()) return undefined;
    let remove;

    const clearDelivered = async () => {
      try {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        await FirebaseMessaging.removeAllDeliveredNotifications();
      } catch (_) { /* nothing delivered, or permission not granted */ }
      try {
        // Android's foreground path posts local notifications, which are a
        // separate store — clearing one does not clear the other.
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        const { notifications } = await LocalNotifications.getDeliveredNotifications();
        if (notifications?.length) {
          await LocalNotifications.removeDeliveredNotifications({ notifications });
        }
      } catch (_) { /* plugin unavailable on this platform */ }
    };

    clearDelivered();

    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) clearDelivered();
        });
        remove = () => handle.remove();
      } catch (_) { /* plugin missing — the launch-time clear still ran */ }
    })();

    return () => { if (remove) remove(); };
  }, []);

  // ── Android hardware back button ──────────────────────────────────────────
  useEffect(() => {
    if (!isNative() || !isAndroid()) return;
    let remove;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('backButton', ({ canGoBack }) => {
          const path = window.location.pathname;
          // Deeper screens: go back through history.
          if (!ROOT_PATHS.includes(path) && canGoBack) { window.history.back(); return; }
          // Deep screen with NO history — the usual way in is a notification
          // tap, which navigates with location.href and so starts a fresh
          // history stack. Without this, back on that screen offered to exit
          // the app instead of going to the dashboard.
          if (!ROOT_PATHS.includes(path)) { window.location.href = '/dashboard'; return; }
          // Root screens: double-press within 2s to exit, otherwise hint.
          const now = Date.now();
          if (now - lastBackRef.current < 2000) { App.exitApp(); return; }
          lastBackRef.current = now;
          setToast('Press back again to exit');
          setTimeout(() => setToast(''), 2000);
        });
        remove = () => handle.remove();
      } catch (_) { /* plugin missing — ignore */ }
    })();
    return () => { if (remove) remove(); };
  }, []);

  if (!toast) return null;
  return (
    <div style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom: 'calc(84px + env(safe-area-inset-bottom))', zIndex: 99999,
      background: 'rgba(0,0,0,0.82)', color: '#fff', padding: '10px 18px',
      borderRadius: 100, fontSize: 14, fontWeight: 600,
      boxShadow: '0 4px 16px rgba(0,0,0,0.3)', pointerEvents: 'none',
    }}>
      {toast}
    </div>
  );
};

export default NativeBridge;
